import React, { useState } from 'react';
import { Search, Filter, MapPin, Phone, Mail, Heart, Calendar, ChevronRight, TrendingUp, MessageSquare, Plus, Sparkles, Users as UsersIcon, Star, RefreshCw, BadgeCheck, Eye, MousePointerClick, Megaphone, Target, Zap, Activity } from 'lucide-react';
import { properties } from '../data/properties';
import PropertyCard from './PropertyCard';
import PropertyDetailModal from './PropertyDetailModal';

/* Generic page wrapper with header */
function PageHeader({ section, title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between mb-6 mt-4 relative z-10">
      <div>
        <div className="font-body text-[9px] tracking-[0.32em] text-[#8E4A23] font-semibold uppercase mb-2">
          {section}
        </div>
        <h2 className="font-display text-[28px] text-[#1C130D] font-light tracking-tight leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="font-body text-[12px] text-[#5A2F12]/60 mt-1.5">{subtitle}</p>
        )}
      </div>
      {action && (
        <button
          className="px-5 py-2.5 rounded-full flex items-center gap-2 font-display text-[12px] tracking-[0.18em] uppercase font-semibold transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(135deg, #E08A4F, #C56C39 50%, #8E4A23)',
            color: '#1C130D',
            boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset, 0 6px 16px -4px rgba(197,108,57,0.45)',
          }}
        >
          <Plus size={14} /> {action}
        </button>
      )}
    </div>
  );
}

/* Light card wrapper */
function LightCard({ children, className = '', testId }) {
  return (
    <div
      className={`rounded-3xl p-6 ${className}`}
      style={{
        background: 'linear-gradient(135deg, rgba(255,250,242,0.96), rgba(244,236,223,0.92))',
        border: '1px solid rgba(197,108,57,0.30)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 16px 40px -12px rgba(0,0,0,0.18)',
      }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/* Dark card wrapper for items needing neon legibility */
function DarkCard({ children, className = '', testId }) {
  return (
    <div
      className={`rounded-3xl p-6 ${className}`}
      style={{
        background: 'linear-gradient(135deg, rgba(15,8,4,0.92), rgba(28,19,13,0.85))',
        border: '1px solid rgba(197,108,57,0.40)',
        boxShadow: '0 1px 0 rgba(244,213,184,0.10) inset, 0 16px 40px -12px rgba(0,0,0,0.5)',
      }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/* ============ PORTFÖY LİSTESİ ============ */
export function PortfolioListPage({ role }) {
  const [openProperty, setOpenProperty] = useState(null);
  return (
    <div data-testid="page-portfolio">
      <PageHeader
        section="Portföy Listesi"
        title={role === 'yonetici' ? 'Tüm Ofis Portföyü · 247 ilan' : 'Portföyüm · 38 ilan'}
        subtitle={role === 'yonetici' ? 'Tüm danışmanların aktif portföyü' : 'Sizin yönetiminizdeki ilanlar'}
        action="Yeni İlan"
      />

      <LightCard className="mb-6" testId="portfolio-filter">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search size={16} className="text-[#8E4A23]" />
            <input className="flex-1 bg-transparent border-0 outline-none text-[#1C130D] placeholder:text-[#5A2F12]/40 font-body text-[14px]" placeholder="Portföy kodu, başlık, lokasyon..." />
          </div>
          {['Tümü', 'Satılık', 'Kiralık', 'Yatırımlık', 'Premium'].map((f, i) => (
            <button key={f} className={`capsule ${i === 0 ? 'capsule-copper' : ''}`} style={i !== 0 ? { background: 'rgba(28,19,13,0.06)', color: '#5A2F12', border: '1px solid rgba(28,19,13,0.15)' } : {}}>
              {f}
            </button>
          ))}
        </div>
      </LightCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {properties.map((p, i) => (
          <PropertyCard key={p.id} property={p} index={i} />
        ))}
      </div>
    </div>
  );
}

/* ============ MÜŞTERİLER ============ */
const customers = [
  { id: 1, name: 'Ayşe Kaya', tag: 'Premium', match: 4, budget: '12-18M ₺', looking: 'Lara · 3+1 deniz manzaralı', last: '2 saat önce', initials: 'AK' },
  { id: 2, name: 'Cenk Erdem', tag: 'Yatırımcı', match: 7, budget: '20-30M ₺', looking: 'Konyaaltı villa · havuzlu', last: 'Bugün', initials: 'CE' },
  { id: 3, name: 'Selin Aydın', tag: 'İlk Ev', match: 2, budget: '5-8M ₺', looking: 'Kepez 2+1 metro yakını', last: 'Dün', initials: 'SA' },
  { id: 4, name: 'Doğa Yılmaz', tag: 'Kiracı', match: 3, budget: '40-60K ₺/ay', looking: 'Muratpaşa eşyalı 1+1', last: '3 gün', initials: 'DY' },
  { id: 5, name: 'Mert Aslan', tag: 'Premium', match: 5, budget: '30-50M ₺', looking: 'Çeşme villa', last: '1 saat', initials: 'MA' },
  { id: 6, name: 'Zeynep Demir', tag: 'Yatırımcı', match: 6, budget: '15-25M ₺', looking: 'Belek penthouse', last: 'Bugün', initials: 'ZD' },
];

export function CustomersPage({ role }) {
  return (
    <div data-testid="page-customers">
      <PageHeader
        section="Müşteriler"
        title={role === 'yonetici' ? 'Tüm Ofis Müşterileri · 23.335' : 'Müşterilerim · 1.842'}
        subtitle="AI destekli profil, talep eşleştirme ve takip"
        action="Yeni Müşteri"
      />

      <div className="grid grid-cols-3 gap-5">
        {customers.map(c => (
          <LightCard key={c.id} testId={`customer-${c.id}`}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-display font-semibold text-[#1C130D] flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #E08A4F, #C56C39)', boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset' }}>
                {c.initials}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-display text-[15px] font-semibold text-[#1C130D]">{c.name}</h4>
                <span className="capsule capsule-copper inline-flex mt-1">{c.tag}</span>
              </div>
              <div className="text-right">
                <div className="font-body text-[9px] text-[#5A2F12]/55 uppercase tracking-wider">Eşleşme</div>
                <div className="font-display text-[18px] text-[#2A9669] font-semibold">{c.match}</div>
              </div>
            </div>
            <div className="space-y-2 text-[12px]">
              <div className="flex items-baseline gap-2">
                <span className="font-body text-[9px] text-[#5A2F12]/55 uppercase tracking-wider w-16 flex-shrink-0">Bütçe</span>
                <span className="font-display text-[#1C130D] font-medium">{c.budget}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-body text-[9px] text-[#5A2F12]/55 uppercase tracking-wider w-16 flex-shrink-0">Aranan</span>
                <span className="font-body text-[#3A2A1F]">{c.looking}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-body text-[9px] text-[#5A2F12]/55 uppercase tracking-wider w-16 flex-shrink-0">Son</span>
                <span className="font-body text-[#5A2F12]/65">{c.last}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#1C130D]/10">
              <button className="flex-1 py-2 rounded-full font-display text-[10px] tracking-[0.18em] uppercase font-semibold flex items-center justify-center gap-1.5"
                style={{ background: 'rgba(28,19,13,0.08)', color: '#1C130D' }}>
                <Phone size={11}/> Ara
              </button>
              <button className="flex-1 py-2 rounded-full font-display text-[10px] tracking-[0.18em] uppercase font-semibold flex items-center justify-center gap-1.5"
                style={{ background: 'rgba(197,108,57,0.18)', color: '#5A2F12' }}>
                <MessageSquare size={11}/> Mesaj
              </button>
            </div>
          </LightCard>
        ))}
      </div>
    </div>
  );
}

/* ============ AKILLI EŞLEŞTİRME ============ */
export function SmartMatchingPage({ role }) {
  const matches = [
    { id: 1, customer: 'Ayşe Kaya', property: properties[0], score: 96, reason: 'Lokasyon · Bütçe · Manzara' },
    { id: 2, customer: 'Cenk Erdem', property: properties[1], score: 94, reason: 'Villa · Havuz · Konum' },
    { id: 3, customer: 'Mert Aslan', property: properties[5], score: 91, reason: 'Lüks · Yatırım Skoru' },
    { id: 4, customer: 'Zeynep Demir', property: properties[2], score: 88, reason: 'Penthouse · Yatırım' },
  ];
  return (
    <div data-testid="page-matching">
      <PageHeader
        section="Akıllı Eşleştirme"
        title="AI Destekli Eşleştirme · 12 yeni"
        subtitle="Müşteri talepleri ile portföy AI puanlama bazlı eşleşmeler"
        action="Tarat"
      />

      <div className="space-y-4">
        {matches.map(m => (
          <LightCard key={m.id} testId={`match-${m.id}`}>
            <div className="grid grid-cols-12 gap-5 items-center">
              <div className="col-span-1 text-center">
                <div className="font-display text-[28px] copper-shine font-bold leading-none">%{m.score}</div>
                <div className="font-body text-[8px] text-[#5A2F12]/55 uppercase tracking-wider mt-1">AI Skor</div>
              </div>
              <div className="col-span-3">
                <div className="font-body text-[9px] text-[#8E4A23] uppercase tracking-wider mb-1">Müşteri</div>
                <div className="font-display text-[16px] text-[#1C130D] font-semibold">{m.customer}</div>
              </div>
              <div className="col-span-1 text-center">
                <ChevronRight size={20} className="text-[#C56C39] mx-auto" />
              </div>
              <div className="col-span-3">
                <div className="font-body text-[9px] text-[#8E4A23] uppercase tracking-wider mb-1">Portföy</div>
                <div className="font-display text-[14px] text-[#1C130D] font-medium leading-tight">{m.property.title}</div>
                <div className="font-body text-[10px] text-[#5A2F12]/60 mt-0.5">{m.property.code} · {m.property.price}</div>
              </div>
              <div className="col-span-2">
                <div className="font-body text-[9px] text-[#8E4A23] uppercase tracking-wider mb-1">Sebep</div>
                <div className="font-body text-[11px] text-[#3A2A1F]">{m.reason}</div>
              </div>
              <div className="col-span-2 flex justify-end">
                <button
                  className="px-5 py-2.5 rounded-full flex items-center gap-2 font-display text-[11px] tracking-[0.18em] uppercase font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, #E08A4F, #C56C39 50%, #8E4A23)',
                    color: '#1C130D',
                    boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset',
                  }}
                >
                  <Sparkles size={12}/> Sunum Üret
                </button>
              </div>
            </div>
          </LightCard>
        ))}
      </div>
    </div>
  );
}

/* ============ FAVORİLER ============ */
export function FavoritesPage() {
  return (
    <div data-testid="page-favorites">
      <PageHeader section="Favoriler" title="Beğenilenler · 18 ilan" subtitle="Müşterilerinizin ve ekibin favorilediği portföy" />
      <div className="grid grid-cols-3 gap-5">
        {properties.slice(0, 3).map(p => (
          <DarkCard key={p.id}>
            <div className="relative h-40 -m-6 mb-4 overflow-hidden rounded-t-3xl">
              <img src={p.image} alt={p.title} className="w-full h-full object-cover"/>
              <button className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(15,8,4,0.85)', border: '1px solid rgba(197,108,57,0.5)' }}>
                <Heart size={14} className="fill-[#E08A4F] text-[#E08A4F]"/>
              </button>
            </div>
            <h4 className="font-display text-[15px] text-[#F4ECDF] font-medium mb-1">{p.title}</h4>
            <div className="font-display copper-shine text-[18px] font-semibold mb-3">{p.price}</div>
            <div className="font-body text-[10px] text-[#F4ECDF]/45 uppercase tracking-wider flex items-center gap-1.5">
              <Star size={10} className="text-[#F7D002] fill-[#F7D002]"/> 12 favori · 4 müşteri
            </div>
          </DarkCard>
        ))}
      </div>
    </div>
  );
}

/* ============ HARİTA ============ */
export function MapPage() {
  return (
    <div data-testid="page-map">
      <PageHeader section="Harita Merkezi" title="Coğrafi Portföy Görünümü" subtitle="Bölge bazlı ısı haritası ve canlı konum analizi" />

      {/* Reference layout — Antalya canlı emsal */}
      <DarkCard className="p-0 overflow-hidden">
        <div className="relative">
          <img
            src="/assets/refs/harita_v2.jpg"
            alt="Antalya harita merkezi · emsal"
            className="w-full block"
            style={{ display: 'block' }}
          />
          <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full"
            style={{
              background: 'rgba(15,8,4,0.85)',
              border: '1px solid rgba(197,108,57,0.45)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <span className="font-display text-[10px] tracking-[0.22em] uppercase text-[#E08A4F] font-semibold">
              EMSAL · 510 portföy
            </span>
          </div>
          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            <span className="capsule capsule-mint">Konyaaltı 64</span>
            <span className="capsule capsule-yellow">Lara 52</span>
            <span className="capsule capsule-copper">Kalkan 38</span>
          </div>
        </div>
      </DarkCard>

      <div className="mt-5 grid sm:grid-cols-3 gap-4">
        {[
          { k: 'Antalya', v: '247 ilan', d: '+18%', desc: 'Konyaaltı · Lara · Belek · Kemer' },
          { k: 'Bodrum',  v: '74 ilan',  d: '+12%', desc: 'Yalıkavak · Türkbükü · Gümüşlük' },
          { k: 'Çeşme',   v: '33 ilan',  d: '+22%', desc: 'Alaçatı · Ilıca · Boyalık' },
        ].map(c => (
          <DarkCard key={c.k} className="p-5">
            <div className="font-body text-[9px] tracking-[0.28em] text-[#E08A4F]/80 uppercase font-semibold">{c.k}</div>
            <div className="flex items-baseline gap-2 mt-1.5">
              <div className="font-display text-[22px] text-[#F4ECDF] font-semibold">{c.v}</div>
              <div className="font-body text-[11px] text-[#68E5B2] font-semibold">{c.d}</div>
            </div>
            <div className="font-body text-[11px] text-[#F4ECDF]/55 mt-2">{c.desc}</div>
          </DarkCard>
        ))}
      </div>
    </div>
  );
}

/* ============ TALEP HAVUZU ============ */
const requests = [
  { id: 1, title: 'Konyaaltı 4+1 deniz manzaralı', budget: '20-25M ₺', date: 'Bugün 14:32', priority: 'high', source: 'Web' },
  { id: 2, title: 'Belek villa havuzlu', budget: '35M+ ₺', date: 'Bugün 11:18', priority: 'high', source: 'Partner' },
  { id: 3, title: 'Lara penthouse 3+1', budget: '15-18M ₺', date: 'Dün', priority: 'medium', source: 'Web' },
  { id: 4, title: 'Muratpaşa kiralık 2+1', budget: '40K ₺/ay', date: 'Dün', priority: 'low', source: 'Whatsapp' },
];

export function RequestPoolPage() {
  return (
    <div data-testid="page-requests">
      <PageHeader section="Talep Havuzu" title="Açık Talepler · 8 yeni" subtitle="Otomatik eşleştirme ve danışman atama merkezi" action="Manuel Talep" />
      <div className="space-y-3">
        {requests.map(r => (
          <LightCard key={r.id} className="!py-4" testId={`request-${r.id}`}>
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-1">
                <div className={`w-2.5 h-2.5 rounded-full ${
                  r.priority === 'high' ? 'bg-[#C56C39]' : r.priority === 'medium' ? 'bg-[#F7D002]' : 'bg-[#68E5B2]'
                }`} />
              </div>
              <div className="col-span-5">
                <div className="font-display text-[14px] text-[#1C130D] font-semibold">{r.title}</div>
                <div className="font-body text-[10px] text-[#5A2F12]/60 mt-0.5">{r.source} · {r.date}</div>
              </div>
              <div className="col-span-2">
                <div className="font-body text-[9px] text-[#8E4A23] uppercase tracking-wider">Bütçe</div>
                <div className="font-display text-[13px] text-[#1C130D] font-medium">{r.budget}</div>
              </div>
              <div className="col-span-2">
                <span className="capsule capsule-copper"><Sparkles size={9}/> AI Eşle</span>
              </div>
              <div className="col-span-2 flex justify-end">
                <button className="px-4 py-2 rounded-full font-display text-[10px] tracking-[0.18em] uppercase font-semibold"
                  style={{ background: 'rgba(28,19,13,0.08)', color: '#1C130D' }}>
                  Atama Yap
                </button>
              </div>
            </div>
          </LightCard>
        ))}
      </div>
    </div>
  );
}

/* ============ ENTEGRASYONLAR ============ */
const integrations = [
  { name: 'Meta Ads', desc: 'Facebook & Instagram reklam yayını', status: 'Bağlı', color: '#1877F2' },
  { name: 'WhatsApp Business', desc: 'Müşteri DM otomasyonu', status: 'Bağlı', color: '#25D366' },
  { name: 'Sahibinden.com', desc: 'Otomatik ilan senkronizasyonu', status: 'Bekliyor', color: '#FFB800' },
  { name: 'Hepsiemlak', desc: 'Portföy paylaşım kanalı', status: 'Bağlı değil', color: '#E94560' },
  { name: 'Google Calendar', desc: 'Randevu & görüşme takvimi', status: 'Bağlı', color: '#4285F4' },
  { name: 'Stripe', desc: 'Online ödeme & komisyon', status: 'Bağlı değil', color: '#635BFF' },
];

export function IntegrationsPage() {
  return (
    <div data-testid="page-integrations">
      <PageHeader section="Entegrasyonlar" title="Meta, DM ve Platform Bağlantıları" subtitle="DEO'yu kullandığınız diğer servislerle eşleştirin" />
      <div className="grid grid-cols-3 gap-5">
        {integrations.map(i => (
          <LightCard key={i.name} testId={`integration-${i.name.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold text-white text-[14px]"
                style={{ background: i.color, boxShadow: '0 1px 0 rgba(255,255,255,0.3) inset' }}>
                {i.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="font-display text-[14px] text-[#1C130D] font-semibold">{i.name}</div>
                <div className="font-body text-[10px] uppercase tracking-wider mt-0.5"
                  style={{
                    color: i.status === 'Bağlı' ? '#2A9669' : i.status === 'Bekliyor' ? '#B8860B' : '#5A2F12',
                    opacity: i.status === 'Bağlı değil' ? 0.5 : 1,
                  }}>
                  {i.status === 'Bağlı' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2A9669] mr-1"/>}
                  {i.status}
                </div>
              </div>
            </div>
            <div className="font-body text-[12px] text-[#3A2A1F]/75 mb-4 leading-relaxed">{i.desc}</div>
            <button className="w-full py-2.5 rounded-full font-display text-[11px] tracking-[0.2em] uppercase font-semibold"
              style={{
                background: i.status === 'Bağlı' ? 'rgba(28,19,13,0.08)' : 'linear-gradient(135deg, #E08A4F, #C56C39)',
                color: i.status === 'Bağlı' ? '#1C130D' : '#1C130D',
              }}>
              {i.status === 'Bağlı' ? 'Yönet' : 'Bağlan'}
            </button>
          </LightCard>
        ))}
      </div>
    </div>
  );
}


/* ============ CRM PAGE ============ */
export function CRMPage() {
  const pipeline = [
    { stage: 'Yeni Talep', count: 38, color: '#68E5B2', items: ['Ayşe T. — Lara 4+1', 'Mehmet K. — Konyaaltı villa', 'Selin B. — Kalkan loft'] },
    { stage: 'Görüşme', count: 24, color: '#F7D002', items: ['Burak Ö. — Belek rezidans', 'Cansu H. — Side daire'] },
    { stage: 'Sunum', count: 12, color: '#E08A4F', items: ['Onur D. — Kemer köşk', 'Pınar A. — Muratpaşa penthouse'] },
    { stage: 'Pazarlık', count: 6, color: '#C56C39', items: ['Volkan E. — Lara rezidans'] },
    { stage: 'Kapanış', count: 3, color: '#68E5B2', items: ['Deniz S. — Kalkan villa (final)'] },
  ];

  return (
    <div className="relative" data-testid="crm-page">
      <PageHeader
        section="Müşteri İlişkileri"
        title="CRM Pipeline"
        subtitle="Tüm müşteri etkileşimleri tek panelde — taleplerinden kapanışa kadar"
        action="Yeni Müşteri"
      />

      {/* Pipeline KPI'lar */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {pipeline.map(s => (
          <DarkCard key={s.stage} className="!p-4">
            <div className="font-body text-[9px] tracking-[0.22em] uppercase text-[#F4ECDF]/55 font-semibold mb-2">
              {s.stage}
            </div>
            <div className="flex items-end gap-2">
              <div className="font-display text-[28px] font-bold tracking-tight" style={{ color: s.color }}>
                {s.count}
              </div>
              <div className="font-body text-[10px] text-[#F4ECDF]/55 mb-1">müşteri</div>
            </div>
            <div className="h-1 rounded-full mt-2.5 overflow-hidden bg-white/8">
              <div className="h-full rounded-full" style={{ width: `${Math.min(s.count * 3, 100)}%`, background: s.color }} />
            </div>
          </DarkCard>
        ))}
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {pipeline.map(s => (
          <LightCard key={s.stage} className="!p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-body text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: s.color }}>
                {s.stage}
              </div>
              <span className="font-display text-[11px] text-[#5A2F12]/60 font-semibold">{s.count}</span>
            </div>
            <div className="space-y-2">
              {s.items.map((it, i) => (
                <div key={i} className="rounded-xl p-3"
                  style={{
                    background: 'rgba(255,250,242,0.85)',
                    border: '1px solid rgba(197,108,57,0.25)',
                  }}
                >
                  <div className="font-display text-[12px] text-[#1C130D] font-medium leading-snug">{it}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="font-body text-[8.5px] tracking-[0.18em] uppercase text-[#8E4A23] font-semibold">Aktif</span>
                    <span className="font-body text-[9px] text-[#5A2F12]/55">· {Math.floor(Math.random() * 7) + 1}d önce</span>
                  </div>
                </div>
              ))}
            </div>
          </LightCard>
        ))}
      </div>

      {/* Hızlı Aksiyonlar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {[
          { icon: MessageSquare, title: 'WhatsApp Bildirimleri', desc: 'Otomatik takip mesajları · DEO şablonlarıyla', count: '142 bekliyor' },
          { icon: Phone, title: 'Geri Arama Listesi', desc: '24 saat içinde aranması gereken müşteriler', count: '38 müşteri' },
          { icon: Star, title: 'VIP Müşteriler', desc: 'Yıllık 50M+ portföy aktif eden segment', count: '12 aktif' },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <DarkCard key={i} className="!p-5 flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(224,138,79,0.18)', border: '1px solid rgba(224,138,79,0.4)' }}>
                <Icon size={18} className="text-[#E08A4F]" />
              </div>
              <div className="flex-1">
                <div className="font-display text-[14px] text-[#F4ECDF] font-medium mb-0.5">{c.title}</div>
                <div className="font-body text-[11px] text-[#F4ECDF]/55 leading-snug mb-2">{c.desc}</div>
                <div className="font-body text-[10px] tracking-[0.20em] uppercase font-semibold text-[#68E5B2]">{c.count}</div>
              </div>
            </DarkCard>
          );
        })}
      </div>
    </div>
  );
}

/* ============ REKLAM PANELİ ============ */
export function AdsPanelPage() {
  const channels = [
    { name: 'Google Ads', spent: '₺ 42.500', impressions: '184K', clicks: '4.2K', cpa: '₺ 280', color: '#E08A4F', live: true },
    { name: 'Meta (FB & IG)', spent: '₺ 38.200', impressions: '212K', clicks: '5.8K', cpa: '₺ 195', color: '#68E5B2', live: true },
    { name: 'Sahibinden Premium', spent: '₺ 18.900', impressions: '92K', clicks: '2.1K', cpa: '₺ 320', color: '#F7D002', live: true },
    { name: 'Hürriyet Emlak', spent: '₺ 12.400', impressions: '64K', clicks: '1.4K', cpa: '₺ 410', color: '#C56C39', live: false },
  ];

  const activeCampaigns = [
    { title: 'Lara Deniz Manzaralı Rezidans — Sezon', status: 'Yayında', budget: '₺ 24K', performance: '+38%', tone: 'mint' },
    { title: 'Konyaaltı Villa — Premium Hedefleme', status: 'Yayında', budget: '₺ 18K', performance: '+22%', tone: 'mint' },
    { title: 'Kemer Köşk — VIP Retargeting', status: 'Test', budget: '₺ 8K', performance: '+5%', tone: 'yellow' },
    { title: 'Belek Yatırımlık — Lookalike', status: 'Duraklatıldı', budget: '₺ 12K', performance: '-3%', tone: 'copper' },
  ];

  return (
    <div className="relative" data-testid="ads-panel-page">
      <PageHeader
        section="Pazarlama"
        title="Reklam Paneli"
        subtitle="Tüm kampanyaların performansı, kanal dağılımı ve AI önerileri"
        action="Yeni Kampanya"
      />

      {/* KPI Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Toplam Harcama', value: '₺ 112K', delta: '+18.4%', icon: TrendingUp, color: '#E08A4F' },
          { label: 'Gösterim', value: '552K', delta: '+12.2%', icon: Eye, color: '#F7D002' },
          { label: 'Tıklama', value: '13.5K', delta: '+8.7%', icon: MousePointerClick, color: '#68E5B2' },
          { label: 'Ort. CPA', value: '₺ 268', delta: '-9.4%', icon: Target, color: '#68E5B2' },
        ].map(k => {
          const I = k.icon;
          return (
            <DarkCard key={k.label} className="!p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-body text-[9px] tracking-[0.22em] uppercase text-[#F4ECDF]/55 font-semibold">
                  {k.label}
                </div>
                <I size={14} style={{ color: k.color }} />
              </div>
              <div className="font-display text-[24px] sm:text-[28px] copper-shine font-bold tracking-tight leading-none">
                {k.value}
              </div>
              <div className="font-body text-[11px] mt-1.5" style={{ color: k.color }}>
                {k.delta}
              </div>
            </DarkCard>
          );
        })}
      </div>

      {/* Channels + AI öneri */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <DarkCard className="!p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-[16px] text-[#F4ECDF] font-medium">Kanal Performansı</div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#68E5B2] animate-pulse" />
                <span className="font-body text-[9px] tracking-[0.20em] uppercase text-[#68E5B2] font-semibold">Canlı</span>
              </div>
            </div>
            <div className="space-y-3">
              {channels.map(c => (
                <div key={c.name} className="grid grid-cols-12 items-center gap-3 py-2.5 border-b border-white/8 last:border-b-0">
                  <div className="col-span-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color, boxShadow: `0 0 6px ${c.color}` }} />
                    <span className="font-display text-[12.5px] text-[#F4ECDF] font-medium tracking-tight">{c.name}</span>
                    {c.live && (
                      <span className="font-body text-[8px] tracking-[0.18em] uppercase text-[#68E5B2]/85 font-semibold">Aktif</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    <div className="font-body text-[8.5px] uppercase tracking-[0.18em] text-[#F4ECDF]/45">Harcama</div>
                    <div className="font-display text-[12px] text-[#F4ECDF] font-medium">{c.spent}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="font-body text-[8.5px] uppercase tracking-[0.18em] text-[#F4ECDF]/45">Gösterim</div>
                    <div className="font-display text-[12px] text-[#F4ECDF] font-medium">{c.impressions}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="font-body text-[8.5px] uppercase tracking-[0.18em] text-[#F4ECDF]/45">Tıklama</div>
                    <div className="font-display text-[12px] text-[#F4ECDF] font-medium">{c.clicks}</div>
                  </div>
                  <div className="col-span-3 text-right">
                    <div className="font-body text-[8.5px] uppercase tracking-[0.18em] text-[#F4ECDF]/45">CPA</div>
                    <div className="font-display text-[12px] copper-shine font-bold">{c.cpa}</div>
                  </div>
                </div>
              ))}
            </div>
          </DarkCard>
        </div>

        {/* AI Öneri */}
        <DarkCard className="!p-5 relative overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={14} className="text-[#F7D002]" />
            <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#F7D002] font-semibold">DEO AI Önerisi</div>
          </div>
          <div className="font-display text-[14px] text-[#F4ECDF] font-light leading-[1.5] mb-4">
            <span className="copper-shine font-semibold italic">"Konyaaltı villa"</span> kampanyasının CTR'si %38 arttı.
            Bütçeyi <span className="text-[#68E5B2] font-semibold">+₺ 6.000</span> artırırsan, 11 ek nitelikli müşteri tahmin ediliyor.
          </div>
          <button className="w-full px-4 py-2.5 rounded-full flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #E08A4F 0%, #C56C39 50%, #8E4A23 100%)',
              color: '#1C130D',
              boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset, 0 6px 14px -4px rgba(197,108,57,0.5)',
            }}
            data-testid="ads-ai-apply"
          >
            <span className="font-display text-[11px] tracking-[0.18em] uppercase font-semibold">Uygula</span>
            <ChevronRight size={12} />
          </button>
        </DarkCard>
      </div>

      {/* Aktif kampanyalar */}
      <DarkCard className="!p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-[16px] text-[#F4ECDF] font-medium">Aktif Kampanyalar</div>
          <button className="font-body text-[10px] tracking-[0.20em] uppercase text-[#E08A4F] font-semibold hover:text-[#F7D002] transition-colors">
            Tümünü Gör
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {activeCampaigns.map((c, i) => (
            <div key={i} className="rounded-2xl p-4"
              style={{
                background: 'rgba(244,213,184,0.04)',
                border: '1px solid rgba(197,108,57,0.30)',
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="font-display text-[13px] text-[#F4ECDF] font-medium leading-snug flex-1 pr-2">
                  {c.title}
                </div>
                <span className={`capsule capsule-${c.tone} text-[9px]`} style={{ padding: '4px 9px' }}>
                  {c.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <div>
                  <div className="font-body text-[8.5px] tracking-[0.18em] uppercase text-[#F4ECDF]/45 font-semibold">Bütçe</div>
                  <div className="font-display text-[14px] copper-shine font-bold">{c.budget}</div>
                </div>
                <div className="text-right">
                  <div className="font-body text-[8.5px] tracking-[0.18em] uppercase text-[#F4ECDF]/45 font-semibold">Performans</div>
                  <div className="font-display text-[14px] font-bold" style={{ color: c.performance.startsWith('+') ? '#68E5B2' : '#C56C39' }}>
                    {c.performance}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DarkCard>
    </div>
  );
}
